/**
 * Utz Gifts: the order's card (contract Revision 4, utz-gifts-fulfillment/docs/contract.md).
 *
 * The buyer picks one card (or none) and an optional message for the whole order; the same card
 * and message go in every box. The choice lives here as window.CorpCard and is shown by the "Card"
 * block on the product page (snippets/corp-card-picker.liquid) and by the multiple-recipients
 * dialog (assets/corp-recipients.js); changing either changes both.
 *
 * The cart holds it as at most one line with `_gift_card: order` (quantity 1). After every add to
 * cart, the cart's card is made to match the choice: the theme's ProductForm awaits
 * FoxTheme.corpCard.afterAdd(response, formData) before it refreshes the cart drawer, and the
 * dialog replaces the card line in its own add. With boxes already in the cart, the block offers
 * "Update the card in your cart" when the choice differs from the cart's card. Each card can be
 * shown larger (CorpCard.zoom), from the block or from the dialog.
 */
(function (global) {
  'use strict';

  if (global.CorpCard) return;

  const LIMITS = { message: 250, lines: 6 };
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const CARD_ERROR = "Your boxes are in your cart, but we couldn't update the gift label. Please try again.";

  function normalizeMessage(value) {
    return String(value == null ? '' : value).replace(/\r\n?/g, '\n').replace(/[ \t]+$/gm, '').trim();
  }

  /** The dialog's rules: 250 characters and 6 lines at most. Returns an error or ''. */
  function messageError(value) {
    const message = normalizeMessage(value);
    if (message.length > LIMITS.message) return `Use ${LIMITS.message} characters or fewer.`;
    if (message.split('\n').length > LIMITS.lines) return `Use ${LIMITS.lines} lines or fewer.`;
    return '';
  }

  function isCardLine(line) {
    return Boolean(line && line.properties && Object.prototype.hasOwnProperty.call(line.properties, '_gift_card'));
  }

  function routes() {
    const r = (global.FoxTheme && global.FoxTheme.routes) || {};
    return {
      cart: r.cart_url || '/cart',
      add: r.cart_add_url || '/cart/add',
      update: r.cart_update_url || '/cart/update'
    };
  }

  async function getCart() {
    const response = await fetch(`${routes().cart}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(CARD_ERROR);
    return response.json();
  }

  async function post(url, body) {
    const response = await fetch(`${url}.js`, { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body) });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.status) {
      throw Object.assign(new Error(result.description || result.message || CARD_ERROR), { cart: true });
    }
    return result;
  }

  // When the last box leaves the cart, assets/corp-cart-guard.js removes the lone card and remembers it
  // here (this tab only), so swapping the only box for another keeps the buyer's card and message.
  const STRIPPED_KEY = 'corp-card:stripped';
  const STRIPPED_MAX_AGE_MS = 6 * 60 * 60 * 1000;
  function readStripped() {
    try {
      const s = JSON.parse(global.sessionStorage.getItem(STRIPPED_KEY) || 'null');
      if (!s || !s.variantId || !(Date.now() - s.at < STRIPPED_MAX_AGE_MS)) return null;
      return { variantId: Number(s.variantId), message: typeof s.message === 'string' ? s.message : '' };
    } catch (e) {
      return null;
    }
  }
  function clearStripped() {
    try { global.sessionStorage.removeItem(STRIPPED_KEY); } catch (e) { /* storage blocked */ }
  }

  const state = { variantId: null, message: '' };
  // What the cart holds: its card (variant and message) and how many boxes. null until known.
  let inCart = null;
  // Whether the buyer changed the card on this page. Until then the cart's card IS the choice, so a
  // page rendered before the cart changed (another tab, the dialog, the drawer) never overwrites it.
  let touched = false;
  // syncCart runs one at a time: an add and "Update the card in your cart" can't interleave.
  let queue = Promise.resolve();
  const listeners = new Set();
  const pickers = new Set();

  function noteCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return;
    const card = cart.items.find(isCardLine);
    inCart = {
      variantId: card ? Number(card.variant_id) : null,
      message: card ? normalizeMessage(card.properties.Message) : '',
      boxes: cart.items.filter((line) => !isCardLine(line)).reduce((sum, line) => sum + line.quantity, 0)
    };
    if (inCart.variantId) clearStripped();
    if (!touched) {
      // A cart with no card but a remembered one (its boxes were swapped) shows that card; a cart with
      // no boxes and no card says nothing about the choice, so the page keeps showing it.
      const stripped = inCart.variantId ? null : readStripped();
      const target = stripped || (inCart.variantId || inCart.boxes > 0 ? inCart : null);
      if (target && (state.variantId !== target.variantId || normalizeMessage(state.message) !== normalizeMessage(target.message))) {
        state.variantId = target.variantId;
        state.message = target.message;
        pickers.forEach((picker) => picker.render());
        listeners.forEach((fn) => fn(CorpCard.get(), 'cart'));
      }
    }
    pickers.forEach((picker) => picker.renderPrompt());
  }

  /** Whether the cart's card already matches the choice (no card counts as matching "No card"). */
  function cartMatches(cart) {
    const lines = cart.items.filter(isCardLine);
    if (!state.variantId) return lines.length === 0;
    return lines.length === 1
      && Number(lines[0].variant_id) === state.variantId
      && lines[0].quantity === 1
      && normalizeMessage(lines[0].properties.Message) === normalizeMessage(state.message);
  }

  /**
   * Makes the cart's card match the choice. extra: { sections, sections_url } for the drawer's
   * HTML. Returns { cart, sections } (sections only when something changed), or throws an Error
   * whose message is for the buyer. A card that can't be added puts the old one back.
   */
  function syncCart(extra) {
    const run = queue.then(() => syncNow(extra));
    queue = run.catch(() => {});
    return run;
  }

  async function syncNow(extra) {
    const urls = routes();
    const before = await getCart();
    // Untouched, the cart's card stands (noteCart adopts it into the choice), unless the cart lost its
    // card because its last box left (the guard remembered it): then that card comes back.
    if (!touched) {
      const stripped = before.items.some(isCardLine) ? null : readStripped();
      if (!stripped) {
        noteCart(before);
        return { cart: before, sections: null };
      }
      state.variantId = stripped.variantId;
      state.message = stripped.message;
    }
    if (cartMatches(before)) {
      noteCart(before);
      return { cart: before, sections: null };
    }
    const removed = before.items.filter(isCardLine);
    let result = null;
    if (removed.length) {
      const updates = {};
      removed.forEach((line) => { updates[line.key] = 0; });
      result = await post(urls.update, Object.assign({ updates }, state.variantId ? {} : extra));
    }
    if (state.variantId) {
      const message = normalizeMessage(state.message);
      const properties = message ? { Message: message, _gift_card: 'order' } : { _gift_card: 'order' };
      try {
        result = await post(urls.add, Object.assign({ items: [{ id: state.variantId, quantity: 1, properties }] }, extra));
      } catch (error) {
        if (removed.length) {
          const back = removed.map((line) => ({ id: line.variant_id, quantity: line.quantity, properties: line.properties }));
          await post(urls.add, { items: back }).catch(() => {});
        }
        throw new Error(error.cart && error.message !== CARD_ERROR
          ? `We couldn't add the gift label: ${error.message}`
          : CARD_ERROR);
      }
    }
    const cart = await getCart();
    noteCart(cart);
    return { cart, sections: (result && result.sections) || null };
  }

  // ---- The enlarged card ------------------------------------------------------------------
  // Each card's zoom button (in the Card block) carries its large image; the dialog shows one
  // card at a time with previous/next and "Choose this card". The recipients dialog opens it too.

  const cardsById = new Map();
  const ICONS = {
    close: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg>',
    prev: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m15 5-7 7 7 7"/></svg>',
    next: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m9 5 7 7-7 7"/></svg>'
  };

  function collectCards(root) {
    root.querySelectorAll('[data-corp-card-zoom]').forEach((button) => {
      const variantId = Number(button.dataset.corpCardZoom);
      if (!cardsById.has(variantId)) {
        cardsById.set(variantId, { variantId, name: button.dataset.name, alt: button.dataset.alt, src: button.dataset.src, srcset: button.dataset.srcset });
      }
    });
  }

  class Lightbox {
    constructor(scheme) {
      const dialog = document.createElement('dialog');
      dialog.className = `corp-card-lightbox color-${scheme || 'scheme-1'}`;
      dialog.setAttribute('aria-labelledby', 'CorpCardLightbox-title');
      dialog.innerHTML = `
        <div class="corp-card-lightbox__inner">
          <div class="corp-card-lightbox__head">
            <h2 class="corp-card-lightbox__title h4" id="CorpCardLightbox-title"></h2>
            <button type="button" class="corp-card-lightbox__close" aria-label="Close" data-close>${ICONS.close}</button>
          </div>
          <div class="corp-card-lightbox__stage">
            <img class="corp-card-lightbox__image" alt="" width="1200" height="1200" sizes="(min-width: 700px) 610px, 100vw" data-image>
            <button type="button" class="corp-card-lightbox__nav corp-card-lightbox__nav--prev" aria-label="Previous label" data-prev>${ICONS.prev}</button>
            <button type="button" class="corp-card-lightbox__nav corp-card-lightbox__nav--next" aria-label="Next label" data-next>${ICONS.next}</button>
          </div>
          <div class="corp-card-lightbox__foot">
            <p class="corp-card-lightbox__count text-sm text-subtext" data-count></p>
            <p class="visually-hidden" aria-live="polite" data-announce></p>
            <p class="corp-card-lightbox__chosen" data-chosen hidden>Selected for every box</p>
            <button type="button" class="btn btn--primary" data-choose><span class="btn__text">Choose this label</span></button>
          </div>
        </div>`;
      document.body.appendChild(dialog);
      this.dialog = dialog;
      const q = (s) => dialog.querySelector(s);
      this.el = { title: q('#CorpCardLightbox-title'), image: q('[data-image]'), count: q('[data-count]'), announce: q('[data-announce]'), chosen: q('[data-chosen]'), choose: q('[data-choose]'), prev: q('[data-prev]'), next: q('[data-next]') };
      q('[data-close]').addEventListener('click', () => dialog.close());
      this.el.prev.addEventListener('click', () => this.step(-1));
      this.el.next.addEventListener('click', () => this.step(1));
      this.el.choose.addEventListener('click', () => this.choose());
      // A click outside the panel lands on the dialog itself (its backdrop).
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1); }
      });
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('corp-card-lightbox-open');
        if (this.opener && this.opener.isConnected) this.opener.focus();
      });
    }

    open(variantId, opener) {
      this.opener = opener || null;
      this.show(variantId);
      document.documentElement.classList.add('corp-card-lightbox-open');
      if (!this.dialog.open) this.dialog.showModal();
      (this.el.choose.hidden ? this.dialog.querySelector('[data-close]') : this.el.choose).focus();
    }

    show(variantId) {
      const list = Array.from(cardsById.values());
      const index = Math.max(0, list.findIndex((c) => c.variantId === variantId));
      const card = list[index];
      this.card = card;
      this.el.title.textContent = `${card.name} label`;
      this.el.image.srcset = card.srcset || '';
      this.el.image.src = card.src;
      // The card's own alt text (Shopify admin) describes the design; the name is the fallback.
      this.el.image.alt = card.alt || `The ${card.name} label design`;
      this.el.count.textContent = list.length > 1 ? `${index + 1} of ${list.length}` : '';
      this.el.prev.hidden = list.length < 2;
      this.el.next.hidden = list.length < 2;
      this.renderChosen();
    }

    renderChosen() {
      const chosen = Boolean(this.card) && state.variantId === this.card.variantId;
      this.el.chosen.hidden = !chosen;
      const hadFocus = document.activeElement === this.el.choose;
      this.el.choose.hidden = chosen;
      if (chosen && hadFocus) this.dialog.querySelector('[data-close]').focus();
    }

    step(delta) {
      const list = Array.from(cardsById.values());
      const index = list.findIndex((c) => c.variantId === this.card.variantId);
      this.show(list[(index + delta + list.length) % list.length].variantId);
      this.el.announce.textContent = `${this.card.name} label, ${this.el.count.textContent}`;
    }

    choose() {
      CorpCard.set({ variantId: this.card.variantId, message: state.message }, 'lightbox');
      const name = this.card.name;
      pickers.forEach((picker) => { picker.status.textContent = `The ${name} label is selected for every box.`; });
      this.dialog.close();
    }
  }

  let lightbox = null;

  const CorpCard = {
    LIMITS,
    normalizeMessage,
    messageError,
    isCardLine,

    /** True when a Card block is on the page, so the choice is the buyer's (not just a default). */
    get active() {
      return pickers.size > 0;
    },

    get() {
      return { variantId: state.variantId, message: state.message };
    },

    /** Changes the choice. origin (a picker or 'dialog') isn't re-rendered, so typing isn't disturbed. */
    set(next, origin) {
      const variantId = next && next.variantId ? Number(next.variantId) : null;
      const message = next && typeof next.message === 'string' ? next.message : state.message;
      if (variantId === state.variantId && message === state.message) return;
      touched = true;
      clearStripped();
      state.variantId = variantId;
      state.message = message;
      pickers.forEach((picker) => { if (picker !== origin) picker.render(); });
      pickers.forEach((picker) => { picker.status.textContent = ''; picker.renderPrompt(); });
      if (lightbox) lightbox.renderChosen();
      listeners.forEach((fn) => fn(CorpCard.get(), origin));
    },

    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },

    /** The cart holds boxes and its card differs from the choice. */
    get needsCartUpdate() {
      if (!inCart || inCart.boxes === 0) return false;
      if (inCart.variantId !== state.variantId) return true;
      return Boolean(state.variantId) && inCart.message !== normalizeMessage(state.message);
    },

    noteCart,
    syncCart,

    /** True once the buyer changed the card on this page; until then the cart's card stands. */
    get touched() {
      return touched;
    },

    /** Re-reads the cart; while untouched, its card becomes the choice. Resolves to the choice. */
    async refreshFromCart() {
      noteCart(await getCart());
      return CorpCard.get();
    },

    /** Whether a card can be shown larger (its Card block tile has an image). */
    canZoom(variantId) {
      return cardsById.has(Number(variantId));
    },

    /** Shows a card larger; opener gets focus back on close. Returns false when it can't. */
    zoom(variantId, opener) {
      if (!cardsById.has(Number(variantId))) return false;
      if (!lightbox) {
        const first = Array.from(pickers)[0];
        lightbox = new Lightbox(first && first.root.dataset.colorScheme);
      }
      lightbox.open(Number(variantId), opener);
      return true;
    },

    /**
     * The ProductForm hook, run after the boxes were added and before the drawer refreshes. Never
     * throws: the boxes are in the cart either way, so a card problem is shown on the block.
     */
    async afterAdd(response, formData) {
      if (!pickers.size) return response;
      const extra = {
        sections: (formData && formData.get('sections')) || '',
        sections_url: (formData && formData.get('sections_url')) || global.location.pathname
      };
      try {
        const out = await syncCart(extra);
        pickers.forEach((picker) => picker.showError(''));
        if (out.sections) return Object.assign({}, response, { sections: out.sections });
      } catch (error) {
        pickers.forEach((picker) => picker.showError(error.message || CARD_ERROR));
      }
      return response;
    }
  };

  /** The product page's Card block. */
  class Picker {
    constructor(root) {
      this.root = root;
      this.formId = root.dataset.formId;
      this.radios = Array.from(root.querySelectorAll('input[name="corp_card"]'));
      this.messageWrap = root.querySelector('[data-corp-card-message]');
      this.message = root.querySelector('textarea');
      this.count = root.querySelector('[data-corp-card-count]');
      this.error = root.querySelector('[data-corp-card-error]');
      this.cardError = root.querySelector('[data-corp-card-cart-error]');
      this.prompt = root.querySelector('[data-corp-card-prompt]');
      this.apply = root.querySelector('[data-corp-card-apply]');
      this.status = root.querySelector('[data-corp-card-status]');

      root.addEventListener('change', (event) => {
        if (event.target.name !== 'corp_card') return;
        CorpCard.set({ variantId: event.target.value, message: this.message.value }, this);
        this.renderMessage();
        this.setFieldError('');
      });
      this.message.addEventListener('input', () => {
        CorpCard.set({ variantId: state.variantId, message: this.message.value }, this);
        this.count.textContent = String(this.message.value.length);
        if (this.message.getAttribute('aria-invalid') === 'true') this.setFieldError(messageError(this.message.value));
      });
      this.apply.addEventListener('click', () => this.updateCart());
      root.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-corp-card-zoom]');
        if (zoom) CorpCard.zoom(zoom.dataset.corpCardZoom, zoom);
      });
    }

    render() {
      const value = state.variantId ? String(state.variantId) : '';
      this.radios.forEach((radio) => { radio.checked = radio.value === value; });
      if (this.message.value !== state.message) this.message.value = state.message;
      this.count.textContent = String(this.message.value.length);
      this.renderMessage();
    }

    renderMessage() {
      this.messageWrap.hidden = !state.variantId;
      if (!state.variantId) this.setFieldError('');
    }

    renderPrompt() {
      this.prompt.hidden = !CorpCard.needsCartUpdate;
    }

    setFieldError(message) {
      this.error.textContent = message;
      this.error.hidden = !message;
      if (message) this.message.setAttribute('aria-invalid', 'true');
      else this.message.removeAttribute('aria-invalid');
    }

    showError(message) {
      this.cardError.textContent = message;
      this.cardError.hidden = !message;
    }

    /** Blocks the add to cart while the message is too long, before the theme sends anything. */
    validate() {
      const error = state.variantId ? messageError(state.message) : '';
      this.setFieldError(error);
      if (error) this.message.focus();
      return !error;
    }

    async updateCart() {
      if (this.apply.classList.contains('btn--loading') || !this.validate()) return;
      this.apply.classList.add('btn--loading');
      this.apply.setAttribute('aria-disabled', 'true');
      this.showError('');
      try {
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } }));
        const out = await syncCart({ sections: sections.join(','), sections_url: global.location.pathname });
        const theme = global.FoxTheme || {};
        if (out.sections && theme.pubsub) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: Object.assign({}, out.cart, { sections: out.sections }) });
        }
        this.status.textContent = state.variantId ? 'The gift label in your cart is updated.' : 'The gift label was removed from your cart.';
      } catch (error) {
        this.showError(error.message === CARD_ERROR ? "We couldn't update the gift label in your cart. Please try again." : error.message);
      } finally {
        this.apply.classList.remove('btn--loading');
        this.apply.removeAttribute('aria-disabled');
      }
    }
  }

  function init() {
    pickers.forEach((picker) => { if (!picker.root.isConnected) pickers.delete(picker); });
    document.querySelectorAll('[data-corp-card]').forEach((root) => {
      if (root.corpCardPicker) return;
      let data = {};
      try {
        data = JSON.parse(root.querySelector('[data-corp-card-data]').textContent);
      } catch (e) {
        return;
      }
      const picker = new Picker(root);
      root.corpCardPicker = picker;
      collectCards(root);
      // The first picker sets the choice from the cart it was rendered with.
      if (!pickers.size) {
        const cart = data.cart || {};
        inCart = {
          variantId: cart.variant_id ? Number(cart.variant_id) : null,
          message: normalizeMessage(cart.message || ''),
          boxes: cart.boxes || 0
        };
        // An empty cart whose card was just set aside (its last box left) shows that card again.
        const stripped = inCart.variantId || inCart.boxes > 0 ? null : readStripped();
        state.variantId = stripped ? stripped.variantId : inCart.variantId;
        state.message = stripped ? stripped.message : (cart.message || '');
      }
      pickers.add(picker);
      picker.render();
      picker.renderPrompt();
    });
  }

  // Runs before the theme's ProductForm handler (capture on the document): a message that's too
  // long stops the add, so the boxes and the card always go together.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('is') !== 'product-form') return;
    const picker = Array.from(pickers).find((p) => p.formId === form.id);
    if (picker && !picker.validate()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  function subscribe() {
    const theme = global.FoxTheme;
    if (theme && theme.pubsub) {
      theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, (payload) => noteCart(payload && payload.cart));
    }
    // Back/forward restores the page as it was; the cart may have changed since.
    global.addEventListener('pageshow', (event) => {
      if (event.persisted && pickers.size) CorpCard.refreshFromCart().catch(() => {});
    });
  }

  global.CorpCard = CorpCard;
  global.FoxTheme = global.FoxTheme || {};
  global.FoxTheme.corpCard = CorpCard;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { init(); subscribe(); });
  } else {
    init();
    subscribe();
  }
  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})(window);
